import React, { useEffect } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import { Button } from '@mui/material'
import { StoreState } from '@src/manager/reducers'
import ContentHeader from '@src/components/ContentHeader'
import { ConfigDetail } from '@src/manager/models/userDashboard'
import {
  getDefaultConfigAction,
  updateDefaultConfigAction,
  UserConfigActionType,
} from '@src/manager/reducers/userConfig/userConfigActions'
import Snackbar, { SnackbarState } from '@src/components/Snackbar'
import {
  managerPath,
  RouteSection,
  toManagerRoute,
} from '@src/common/appConfig'
import commonMessages from '@common/messages'
import messages from '@manager/utils/messages'
import { ReactComponent as IconSave } from '@src/assets/icons/iconSave.svg'
import ConfigEditForm from './components/ConfigEditForm'
import { BREADCRUMBS } from './utils'
import { useGlobalSnackbar, useLoading } from '@src/hooks'
import { AjaxError } from 'rxjs/ajax'

export default function DefaultConfigEdit() {
  const { formatMessage: f } = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { currentDefaultConfig } = useSelector(
    (state: StoreState) => state.userConfig
  )
  const { id, defaultcfg } = useParams()
  const { control, handleSubmit, reset } = useForm<ConfigDetail>({
    defaultValues: currentDefaultConfig,
  })

  const { status, setSnackbar, clearSnackbar } = useGlobalSnackbar({
    page: RouteSection.UserDashboard,
  })
  const { openSpinner } = useLoading({
    endActions: {
      success: [
        UserConfigActionType.GET_DEFAULT_CONFIG_SUCCESS,
        UserConfigActionType.UPDATE_DEFAULT_CONFIG_SUCCESS,
      ],
      error: [
        UserConfigActionType.GET_DEFAULT_CONFIG_ERROR,
        UserConfigActionType.UPDATE_DEFAULT_CONFIG_ERROR,
      ],
    },
    cbk: {
      success: ({ type }: { type: UserConfigActionType }) => {
        if (type === UserConfigActionType.UPDATE_DEFAULT_CONFIG_SUCCESS) {
          setSnackbar({
            state: SnackbarState.Success,
            message: f(commonMessages.updateSuccess),
            notKeepData: false,
          })
          document.documentElement.scrollTop = 0
          navigate(
            generatePath(toManagerRoute(managerPath.userDashboard.dashboard), {
              id,
            })
          )
        }
      },
      error: ({
        type,
        error,
      }: {
        type: UserConfigActionType
        error: AjaxError
      }) => {
        const message =
          type === UserConfigActionType.UPDATE_DEFAULT_CONFIG_ERROR
            ? f(commonMessages.updateFailed)
            : error.message ?? f(commonMessages.unknownError)
        setSnackbar({
          state: SnackbarState.Error,
          message,
          notKeepData: true,
        })
        document.documentElement.scrollTop = 0
      },
    },
  })

  const onSubmit = (data: ConfigDetail) => {
    openSpinner()
    dispatch(updateDefaultConfigAction({ id: id!, data }))
  }

  useEffect(() => {
    openSpinner()
    dispatch(getDefaultConfigAction({ id: id!, defaultcfg: defaultcfg! }))
  }, [openSpinner, id, defaultcfg, dispatch])

  useEffect(() => {
    if (!!currentDefaultConfig) {
      reset(currentDefaultConfig)
    }
  }, [currentDefaultConfig, reset])

  return (
    <>
      {status && (
        <Snackbar
          severity={SnackbarState.Error}
          message={status.message!}
          onClose={clearSnackbar}
        />
      )}
      {currentDefaultConfig && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <ContentHeader
            title={f(messages.defaultCFGEdit)}
            breadcrumbs={[
              ...BREADCRUMBS,
              {
                title: messages.userDashboard,
                route: generatePath(
                  toManagerRoute(managerPath.userDashboard.dashboard),
                  { id }
                ),
              },
              {
                title: messages.defaultCFGEdit,
              },
            ]}
            buttonList={[
              <Button
                variant="contained"
                type="submit"
                startIcon={<IconSave />}>
                {f(commonMessages.save)}
              </Button>,
            ]}
          />
          <ConfigEditForm config={currentDefaultConfig} control={control} />
        </form>
      )}
    </>
  )
}
